import { Text } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import {
  UseMutationOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"
import { AxiosError } from "axios"

import { AdminRequests, AdminResponses } from "@tfg/types/endpoints/admin"
import {
  organizationFiltersValidationType,
  organizationValidationType,
  updateOrganizationValidationType,
} from "@tfg/types/schemas/admin"

import { httpClient } from "~/backend/axios"
import { queryClient } from "~/backend/queryClient"

export const useGetOrganizationQuery = (id?: string) =>
  useQuery({
    queryKey: ["organizations", id],
    queryFn: async () => {
      const res = await httpClient.get<AdminResponses.Organization>(
        `/admin/organizations/${id}`
      )
      return res.data
    },
  })

export const useListOrganizationsQuery = (
  params?: organizationFiltersValidationType
) =>
  useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const res = await httpClient.get<AdminResponses.Organization[]>(
        "/admin/organizations",
        {
          params,
        }
      )
      return res.data
    },
  })

export const useCreateOrganizationMutation = (
  options?: UseMutationOptions<unknown, AxiosError, organizationValidationType>
) =>
  useMutation({
    ...options,
    mutationFn: async (organization: organizationValidationType) => {
      return await httpClient.post("/admin/organizations", organization)
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
    },
  })

export const useInviteUsersMutation = (
  organizationId: string,
  options?: UseMutationOptions<void, unknown, AdminRequests.InviteUsers>
) =>
  useMutation({
    ...options,
    mutationFn: async (users: AdminRequests.InviteUsers) => {
      await httpClient.post(
        `/admin/organizations/${organizationId}/invite`,
        users
      )
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
    },
  })

export const useUpdateOrganizationMutation = (
  organizationId: string,
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    updateOrganizationValidationType
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async (organization) => {
      return await httpClient.put(
        `/v1/organization/${organizationId}`,
        organization
      )
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      queryClient.invalidateQueries({ queryKey: ["organizations"] })
    },
  })
}

export const useLoginAsUserMutation = (
  options?: UseMutationOptions<
    void,
    unknown,
    {
      userId: string
      forwardRole?: boolean
    }
  >
) =>
  useMutation({
    ...options,
    mutationFn: async ({
      userId,
      forwardRole,
    }: {
      userId: string
      forwardRole?: boolean
    }) => {
      await httpClient.post<{ token: string }>(
        `/admin/users/${userId}/login-as?forwardRole=${forwardRole}`
      )
    },
  })

export const useCreateCountryContact = (forEmails: string[]) => {
  return useMutation<void, AxiosError>({
    mutationFn: async () => {
      notifications.show({
        id: "create-country-contact",
        loading: true,
        title: "Creating country contact",
        message: <Text>country created for {0} users</Text>,
        autoClose: false,
        withCloseButton: true,
      })
      for (const [index, email] of forEmails.entries()) {
        await httpClient.post("admin/createDefaultCountryContact", {
          forEmails: [email],
        })
        let checkIcon = null
        const hasMore = index < forEmails.length - 1
        if (!hasMore) checkIcon = "✅"
        notifications.update({
          id: "create-country-contact",
          loading: true,
          title: "Creating country contact",
          message: (
            <Text>
              country created for {index + 1} users
              {checkIcon}
            </Text>
          ),
          autoClose: !hasMore,
          withCloseButton: true,
        })
      }
    },
  })
}
